// Land/property distribution for Faraid.
//
// Same Quranic fractions that apply to cash also apply to land. This module
// REUSES the share fractions produced by `calculateFaraid` — it does NOT
// re-implement Sunni inheritance math. It only translates fractions into
// per-plot land areas (in the plot's native unit) plus optional BDT-equivalents
// when the user supplies a per-unit price.
//
// Land unit conversions come from `src/data/bdLandUnits.ts` (single source of
// truth — RAJUK / BD government basis: 1 Katha = 720 sq ft, 1 Bigha = 14,400
// sq ft, 1 Decimal = 435.6 sq ft, 1 Acre = 100 Decimal, etc).

import { F } from "./fraction";
import { UNITS_BY_ID, type LandUnit } from "@/data/bdLandUnits";
import type { ShareLine } from "./core";

export type LandTitleStatus =
  | "mutated"        // নামজারি আছে
  | "not-mutated"    // পৈতৃক, নামজারি নেই
  | "disputed"       // বিতর্কিত
  | "other"
  | "";

export type LandType =
  | "agricultural"
  | "residential"
  | "commercial"
  | "ancestral"
  | "other"
  | "";

export interface LandPlot {
  id: string;
  name: string;             // user-facing description
  location: string;         // district / thana
  type: LandType;
  amount: number;           // numeric area in `unitId`
  unitId: string;           // key into UNITS_BY_ID
  pricePerUnit: number;     // BDT per 1 `unitId` (0 = not provided)
  titleStatus: LandTitleStatus;
  indivisible: boolean;     // user flag — physical division not possible
}

export function newPlot(): LandPlot {
  return {
    id: cryptoRandomId(),
    name: "",
    location: "",
    type: "",
    amount: 0,
    unitId: "katha",
    pricePerUnit: 0,
    titleStatus: "",
    indivisible: false,
  };
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `plot-${Math.random().toString(36).slice(2, 10)}`;
}

/** Convert an `amount` in `fromId` to `toId` via square feet. */
export function convertLand(amount: number, fromId: string, toId: string): number {
  const from = UNITS_BY_ID[fromId];
  const to = UNITS_BY_ID[toId];
  if (!from || !to || !Number.isFinite(amount)) return 0;
  return (amount * from.sqft) / to.sqft;
}

export interface PlotAllocation {
  plot: LandPlot;
  amountInPlotUnit: number;     // share of plot in the plot's native unit
  amountInDecimal: number;      // canonical conversion → Decimal (BD standard)
  amountInSqft: number;
  bdtValue: number;             // 0 if pricePerUnit not provided
  /** True when this individual heir's slice is too small to physically divide. */
  flaggedSmall: boolean;
}

export interface LandShareRow {
  /** Mirrors ShareLine identification. */
  groupKey: string;             // `${group}-${type}-${index}` to match ShareLine
  /** Group share (the WHOLE group's fraction, e.g. 7/8 for two sons together). */
  fraction: F;
  /** PER-PERSON share (already divided by count). */
  perPerson: F;
  count: number;
  perPlot: PlotAllocation[];    // PER-PERSON allocation in each plot
  totalDecimalPerPerson: number;
  totalSqftPerPerson: number;
  totalBdtPerPerson: number;
}

export interface LandTotals {
  totalSqft: number;
  totalDecimal: number;
  totalBdt: number;             // sum of plot.pricePerUnit × plot.amount; 0 if any missing
  allValued: boolean;
  /** Per-plot totals for header summary. */
  perPlot: { plot: LandPlot; sqft: number; decimal: number; bdt: number }[];
}

/** Heuristic threshold: any individual share below this is flagged as
 * "physically difficult to divide". 1 Decimal ≈ 435.6 sq ft; 0.5 Katha = 360 sq ft.
 * We use 200 sq ft as a conservative practical floor (≈ a small room). */
export const SMALL_SHARE_SQFT_THRESHOLD = 200;

export function summariseTotals(plots: LandPlot[]): LandTotals {
  let allValued = plots.length > 0;
  const perPlot = plots.map((plot) => {
    const sqft = convertLand(plot.amount, plot.unitId, "sqft");
    const decimal = convertLand(plot.amount, plot.unitId, "decimal");
    const bdt = plot.pricePerUnit > 0 ? plot.pricePerUnit * plot.amount : 0;
    if (plot.pricePerUnit <= 0) allValued = false;
    return { plot, sqft, decimal, bdt };
  });
  return {
    totalSqft: perPlot.reduce((a, p) => a + p.sqft, 0),
    totalDecimal: perPlot.reduce((a, p) => a + p.decimal, 0),
    totalBdt: perPlot.reduce((a, p) => a + p.bdt, 0),
    allValued,
    perPlot,
  };
}

/** Apply per-person share fractions to every plot. */
export function distributeLand(shares: ShareLine[], plots: LandPlot[]): LandShareRow[] {
  return shares.map((line, idx) => {
    const perPerson = line.perPerson;
    const ratio = perPerson.toNumber();

    const perPlot: PlotAllocation[] = plots.map((plot) => {
      const amountInPlotUnit = plot.amount * ratio;
      const amountInDecimal = convertLand(amountInPlotUnit, plot.unitId, "decimal");
      const amountInSqft = convertLand(amountInPlotUnit, plot.unitId, "sqft");
      const bdtValue = plot.pricePerUnit > 0 ? plot.pricePerUnit * amountInPlotUnit : 0;
      const flaggedSmall = amountInSqft > 0 && amountInSqft < SMALL_SHARE_SQFT_THRESHOLD;
      return {
        plot,
        amountInPlotUnit,
        amountInDecimal,
        amountInSqft,
        bdtValue,
        flaggedSmall,
      };
    });

    return {
      groupKey: `${line.group}-${line.type}-${idx}`,
      fraction: line.fraction,
      perPerson,
      count: line.count,
      perPlot,
      totalDecimalPerPerson: perPlot.reduce((a, p) => a + p.amountInDecimal, 0),
      totalSqftPerPerson: perPlot.reduce((a, p) => a + p.amountInSqft, 0),
      totalBdtPerPerson: perPlot.reduce((a, p) => a + p.bdtValue, 0),
    };
  });
}

/** True when ANY heir's individual slice in ANY plot is below the threshold. */
export function hasIndivisibilityRisk(rows: LandShareRow[]): boolean {
  return rows.some((r) => r.perPlot.some((p) => p.flaggedSmall));
}

/** Format a land area in a given unit with sensible precision. */
export function formatLand(amount: number, unit: LandUnit): string {
  if (!Number.isFinite(amount) || amount === 0) return `0 ${unit.symbol ?? unit.id}`;
  const abs = Math.abs(amount);
  let digits = 2;
  if (abs < 0.01) digits = 5;
  else if (abs < 0.1) digits = 4;
  else if (abs < 1) digits = 3;
  else if (abs < 100) digits = 2;
  else digits = 1;
  return amount.toLocaleString("en-US", {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}
